import { useAuth0 } from '../../utils/auth'
import { LDProvider, useLDClient } from 'launchdarkly-react-client-sdk'
import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import AppLoading from '../../components/AppLoading'
import { LaunchDarklyConfig } from '../../data/config'
import Routes from '../../routes'
import { useFormSaveStore } from '../../utils'
import { createLoaders, GlobalDataLoader } from '../../utils/loaders'
import { DegradedModeAlert } from './DegradedModeAlert'

const Login = () => {
    const { isAuthenticated, getAuthenticationData } = useAuth0()
    const resetChanges = useFormSaveStore((state) => state.resetChanges)
    const [secondaryErrors, setSecondaryErrors] = useState<string[]>([])
    const location = useLocation()
    const ldClient = useLDClient()
    const [authDataLoaded, setAuthDataLoaded] = useState(false)
    const [authData, setAuthData] = useState<{
        idToken: string
        accessToken: string
        claims: any
        tokenExpiration?: number
    } | null>(null)

    const handleSecondaryLoaderError = useCallback((id: string, error: Error, message: string) => {
        setSecondaryErrors((prev) => [...prev, message])
        console.error(`Secondary loader failed: ${id}`, error)
        console.warn(`User message: ${message}`)
    }, [])

    const loaders = useMemo(() => createLoaders(), [])

    useEffect(() => {
        const loadAuthData = async () => {
            try {
                const authenticationData = await getAuthenticationData()
                // Handle the authentication data here if needed
                setAuthData(authenticationData)
                // console.log('Authentication data loaded:', authenticationData)
                setAuthDataLoaded(true)
            } catch (error) {
                console.error('Failed to load authentication data:', error)
                setAuthDataLoaded(true) // Set to true even on error to prevent infinite loading
            }
        }

        if (isAuthenticated) {
            loadAuthData()
        }
    }, [getAuthenticationData, isAuthenticated])
    useEffect(() => {
        resetChanges()
    }, [location, resetChanges])
    useEffect(() => {
        // Tracking your memberId lets us know you are connected.
        ldClient?.track(LaunchDarklyConfig.clientId)
    }, [ldClient])

    // Show loading while waiting for authentication data
    if (isAuthenticated && !authDataLoaded) {
        return <div>Loading authentication data...</div>
    }

    if (isAuthenticated && authDataLoaded && authData?.claims) {
        const claims = authData.claims
        if (!claims.email) {
            // Diagnostic for DUCK-2504: some users land in LaunchDarkly with only their auth0 id
            // because the id_token has no `email` claim. Log the claim key list (no values — the
            // values contain PII) so we can see which other/namespaced field carries the address.
            Sentry.logger.info('ld_context.email_missing', {
                sub: claims.sub,
                claim_keys: Object.keys(claims)
            })
        }
        const context = {
            kind: 'user',
            key: claims.sub,
            name: claims.email,
            email: claims.email
        }
        // console.log(context, authData)

        return (
            <Suspense fallback={null}>
                <LDProvider clientSideID={LaunchDarklyConfig.clientId} context={context} timeout={5}>
                    <GlobalDataLoader
                        loaders={loaders}
                        fallback={<AppLoading />}
                        onSecondaryError={handleSecondaryLoaderError}
                    >
                        <DegradedModeAlert errors={secondaryErrors} />
                        <Routes />
                    </GlobalDataLoader>
                </LDProvider>
            </Suspense>
        )
    }

    return <AppLoading />
}

export default Login
